import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowActionsButton,
  AdminSupportingText,
  AdminAlertText,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  AdminLinkAction,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  AdminFormDropdownChevron,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import defaultUserAvatar from '../../../assets/img/User.png';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { createPortal } from 'react-dom';
import {
  collectQueueCases,
  formatQueueDateTime,
  getCaseStatusFilterOptions,
  getSeverityFilterOptions,
  getSupportTeamFilterOptions,
  matchesQueueFilters,
  matchesQueueSearch,
  normalizeSupportTeam,
  QUEUE_SUPPORT_TEAM,
} from '../../../data/moderationQueueSpec';
import { patchModerationCaseOverride } from '../../../utils/moderationCaseSession';

const DEFAULT_FILTERS = {
  status: 'All',
  severity: 'All',
  supportTeam: 'All',
};

/** Case ID · Account · Source · Type · Severity · Case Reason · Support Team · Status · Received · Last Updated · Actions (old order + Support Team) */
const TABLE_GRID =
  'minmax(0, 1.15fr) minmax(0, 1.2fr) minmax(0, 0.75fr) minmax(0, 1.05fr) minmax(0, 0.7fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(62px, auto)';

const CURRENT_ACTOR = 'Alex Chen';

const ModerationQueue = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [severityOpen, setSeverityOpen] = useState(false);
  const [supportTeamOpen, setSupportTeamOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [queueRowActionsOpen, setQueueRowActionsOpen] = useState({});
  const [assignPopup, setAssignPopup] = useState(null);
  const [assignMember, setAssignMember] = useState('');
  const [assignReason, setAssignReason] = useState('');

  const statusRef = useRef(null);
  const severityRef = useRef(null);
  const supportTeamRef = useRef(null);
  const queueRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: queueRowActionsMenuLayout,
    menuRef: queueRowActionsMenuRef,
    placementReady: queueRowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    queueRowActionsOpen,
    queueRowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.DOUBLE
  );

  const toggleQueueRowActions = (rowKey) => {
    setQueueRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(queueRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setQueueRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(severityRef, () => setSeverityOpen(false));
  useClickOutside(supportTeamRef, () => setSupportTeamOpen(false));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const reloadItems = () => {
    setItems(collectQueueCases());
  };

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        reloadItems();
      } catch (err) {
        setError('Failed to load moderation queue. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [location.pathname]);

  const filteredItems = useMemo(() => {
    return items.filter(
      (item) => matchesQueueSearch(item, debouncedSearch) && matchesQueueFilters(item, filters)
    );
  }, [items, debouncedSearch, filters]);

  const sortedItems = useMemo(
    () =>
      [...filteredItems].sort(
        (a, b) => new Date(b.received || b.created || 0) - new Date(a.received || a.created || 0)
      ),
    [filteredItems]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  const totalCount = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(sortedItems.length / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [sortedItems.length]);

  const paginatedItems = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return sortedItems.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [sortedItems, effectivePage]);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  const supportTeamFilterOptions = useMemo(
    () => getSupportTeamFilterOptions(items),
    [items]
  );

  const openCase = (item, e) => {
    if (e) e.stopPropagation();
    navigate(`/admin/moderation/queue/${item.caseId}`, {
      state: { from: location.pathname, item },
    });
  };

  const openAssignPopup = (item, mode) => {
    setQueueRowActionsOpen({});
    setAssignPopup({ item, mode });
    setAssignMember(QUEUE_SUPPORT_TEAM[0] || '');
    setAssignReason('');
  };

  const submitAssignPopup = () => {
    if (!assignPopup?.item || !assignMember.trim() || !assignReason.trim()) return;
    const caseId = assignPopup.item.caseId;
    const now = new Date().toISOString();
    const previous = normalizeSupportTeam(
      assignPopup.item.supportTeam || assignPopup.item.assignedTo
    );
    patchModerationCaseOverride(caseId, {
      status: 'In Progress',
      assignedTo: assignMember.trim(),
      supportTeam: assignMember.trim(),
      lastUpdated: now,
      auditEvents: [
        {
          dateTime: now,
          event: assignPopup.mode === 'reassign' ? 'Reassigned' : 'Assigned',
          details: `${CURRENT_ACTOR}; ${
            previous && previous !== 'Unassigned' ? `${previous} → ` : ''
          }${assignMember.trim()}; ${assignReason.trim()}`,
          relatedRecord: caseId,
        },
      ],
    });
    setAssignPopup(null);
    reloadItems();
  };

  const supportTeamLabel = (item) => {
    const name = normalizeSupportTeam(item.supportTeam || item.assignedTo);
    return !name || name === 'Unassigned' ? '—' : name;
  };

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Moderation Queue</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Moderation Case ID, Flag ID, Account…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search Moderation cases"
            />
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Case Status"
                value={filters.status}
                options={getCaseStatusFilterOptions()}
                onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Severity"
                value={filters.severity}
                options={getSeverityFilterOptions()}
                onChange={(value) => setFilters((prev) => ({ ...prev, severity: value }))}
                menuRef={severityRef}
                open={severityOpen}
                setOpen={setSeverityOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Support Team Member"
                value={filters.supportTeam}
                options={supportTeamFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, supportTeam: value }))}
                menuRef={supportTeamRef}
                open={supportTeamOpen}
                setOpen={setSupportTeamOpen}
                size="lg"
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            {loading ? (
              <div className="py-10 text-center">
                <AdminSupportingText className="text-mediumgray">
                  Loading moderation queue…
                </AdminSupportingText>
              </div>
            ) : error ? (
              <div className="py-10 text-center">
                <AdminAlertText className="text-alertred block">{error}</AdminAlertText>
              </div>
            ) : (
              <div className="min-w-0">
                <div
                  className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                  style={{ gridTemplateColumns: TABLE_GRID }}
                >
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Case ID</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Account</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Source</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Type</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Severity</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Case Reason</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Support Team Member</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Status</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Received</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader>Last Updated</AdminTableHeader>
                  </div>
                  <div className="flex items-center justify-center min-w-0">
                    <AdminTableHeader>Actions</AdminTableHeader>
                  </div>
                </div>

                {sortedItems.length === 0 ? (
                  <div className="py-10 text-center">
                    <AdminSupportingText className="text-mediumgray">
                      No cases match your search or filters.
                    </AdminSupportingText>
                  </div>
                ) : (
                  paginatedItems.map((item) => (
                    <div
                      key={item.caseId}
                      role="button"
                      tabIndex={0}
                      onClick={() => openCase(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openCase(item);
                        }
                      }}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer hover:bg-pastalBlueWhite/50"
                      style={{ gridTemplateColumns: TABLE_GRID }}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminLinkAction
                          type="button"
                          onClick={(e) => openCase(item, e)}
                          title={item.caseId}
                        >
                          {item.caseId}
                        </AdminLinkAction>
                      </div>
                      <div className="flex items-center min-w-0 gap-2">
                        <AdminRowAvatar
                          src={item.accountAvatar || defaultUserAvatar}
                          alt={item.accountName}
                          onError={(e) => {
                            e.target.src = defaultUserAvatar;
                          }}
                        />
                        <AdminTableBody emphasis className="truncate" title={item.accountName}>
                          {item.accountName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate">{item.source}</AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.type}>
                          {item.type}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate">{item.severity}</AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.caseReason}>
                          {item.caseReason}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={supportTeamLabel(item)}>
                          {supportTeamLabel(item)}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminStatusBadge status={item.status} label={item.status} />
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableMeta className="whitespace-nowrap">
                          {formatQueueDateTime(item.received || item.created)}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableMeta className="whitespace-nowrap">
                          {formatQueueDateTime(item.lastUpdated || item.updated)}
                        </AdminTableMeta>
                      </div>
                      <div
                        className="flex items-center justify-center shrink-0 overflow-visible"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            queueRowActionsRefs.current[item.caseId] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleQueueRowActions(item.caseId);
                            }}
                            aria-expanded={Boolean(queueRowActionsOpen[item.caseId])}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {queueRowActionsOpen[item.caseId] && queueRowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={queueRowActionsMenuLayout}
                              menuRef={queueRowActionsMenuRef}
                              placementReady={queueRowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setQueueRowActionsOpen({});
                                  openCase(item, e);
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                              {item.status === 'New' ? (
                                <AdminRowMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAssignPopup(item, 'assign');
                                  }}
                                >
                                  Assign
                                </AdminRowMenuItem>
                              ) : null}
                              {item.status === 'In Progress' ? (
                                <AdminRowMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAssignPopup(item, 'reassign');
                                  }}
                                >
                                  Reassign
                                </AdminRowMenuItem>
                              ) : null}
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {showPagination ? (
            <ConfigurationListPaginationFooter
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              totalCount={totalCount}
              page={effectivePage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          ) : null}
        </DashboardMainContainer>
      </div>

      {assignPopup && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="dashboard-panel font-inter fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
              onClick={() => setAssignPopup(null)}
              role="presentation"
            >
              <div
                className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <AdminPageTitle className="!text-lg text-center">
                  {assignPopup.mode === 'reassign' ? 'Reassign case' : 'Assign case'}
                </AdminPageTitle>
                <div className="mt-6 space-y-4">
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Team member</AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        className={adminFormDetailSelectClasses()}
                        value={assignMember}
                        onChange={(e) => setAssignMember(e.target.value)}
                      >
                        {QUEUE_SUPPORT_TEAM.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Reason</AdminDetailFieldLabel>
                    <textarea
                      className={`${adminFormDetailTextareaClasses()} min-h-[88px]`}
                      value={assignReason}
                      onChange={(e) => setAssignReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <AdminFormSecondaryAction type="button" size="lg" onClick={() => setAssignPopup(null)}>
                    Cancel
                  </AdminFormSecondaryAction>
                  <AdminHeaderAction
                    type="button"
                    size="lg"
                    disabled={!assignMember.trim() || !assignReason.trim()}
                    onClick={submitAssignPopup}
                  >
                    Save
                  </AdminHeaderAction>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </AdminLayout>
  );
};

export default ModerationQueue;
